import { Stack, Typography } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListSubheader from "@mui/material/ListSubheader";
import Skeleton from "@mui/material/Skeleton";
import React, { ReactNode } from "react";

import { LinkItem } from "./LinkItem";

import { LIST_ITEM_ICON_MIN_WIDTH } from "@/layouts/constants";
import { SidebarItem } from "@/layouts/types";

interface LinkListItemProps {
  loading: boolean;
  level: number;
  currentLeftGutterWidth: number;
  item: SidebarItem;
  expandedItems: string[];
  handleSubNavClick(key: string): void;
  currentRightGutterWidth: number;
}

function LinkListItem({
  loading,
  level,
  currentLeftGutterWidth,
  item,
  expandedItems,
  handleSubNavClick,
  currentRightGutterWidth,
}: LinkListItemProps) {
  const { identifier, subItems = [] } = item;

  const py = 4;
  const my = 4;
  const pl = currentLeftGutterWidth + 16;
  const pr = 16;
  const ml = 12;
  const mr = 12;

  const hasKeyInExpandedItems = expandedItems.includes(identifier);

  return loading ? (
    <Skeleton
      key={identifier}
      height={37}
      sx={{
        py: `${py}px`,
        pl: `${pl}px`,
        pr: `${pr}px`,
        my: `${my}px`,
        ml: `${18}px`,
        mr: `${18}px`,
      }}
    />
  ) : (
    <React.Fragment key={identifier}>
      <LinkItem
        item={item}
        handleSubNavClick={() => handleSubNavClick(identifier)}
        hasKeyInExpandedItems={hasKeyInExpandedItems}
        level={level}
        sx={{
          py: `${py}px`,
          pl: `${pl}px`,
          pr: `${pr}px`,
          my: `${my}px`,
          ml: `${ml}px`,
          mr: `${mr}px`,
          color: (theme) => theme.palette.text.primary,
          borderRadius: `8px`,
          opacity: 1,
          "&.Mui-selected": {
            bgcolor: (theme) => theme.palette.sidebar.expandingSidebarCategorySelected,
          },
          "&:hover": {
            bgcolor: (theme) => theme.palette.sidebar.expandingSidebarCategorySelected,
          },
        }}
      />
      {subItems.length > 0 && (
        <Collapse in={hasKeyInExpandedItems} timeout="auto" unmountOnExit={true}>
          <LinkList
            items={subItems}
            level={level + 1}
            item={item}
            currentLeftGutterWidth={currentLeftGutterWidth + LIST_ITEM_ICON_MIN_WIDTH}
            currentRightGutterWidth={currentRightGutterWidth + mr}
            expandedItems={expandedItems}
            handleSubNavClick={handleSubNavClick}
          />
        </Collapse>
      )}
    </React.Fragment>
  );
}

interface Props {
  item: SidebarItem;
  items: SidebarItem[];
  label?: ReactNode | null;
  secondaryLabel?: ReactNode | null;
  currentLeftGutterWidth?: number;
  currentRightGutterWidth?: number;
  level?: number;
  loading?: boolean;
  expandedItems: string[];
  handleSubNavClick(key: string): void;
}

export function LinkList({
  item,
  items,
  label = null,
  secondaryLabel = null,
  currentLeftGutterWidth = 0,
  currentRightGutterWidth = 0,
  level = 0,
  loading = false,
  expandedItems,
  handleSubNavClick,
}: Props) {
  if (!items.length) {
    return (
      <LinkListItem
        key={item.identifier}
        loading={loading}
        level={level}
        currentLeftGutterWidth={currentLeftGutterWidth}
        item={item}
        expandedItems={expandedItems}
        handleSubNavClick={handleSubNavClick}
        currentRightGutterWidth={currentRightGutterWidth}
      />
    );
  }

  return (
    <List
      disablePadding
      sx={{
        position: "relative",
      }}
      subheader={
        label && (
          <ListSubheader
            disableSticky
            sx={{
              mt: 1,
              mb: 1,
              color: (theme) => theme.palette.text.primary,
              fontWeight: "bold",
              fontSize: "0.75rem",
              lineHeight: 2.5,
            }}
          >
            <Stack>
              {label}
              {secondaryLabel && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {secondaryLabel}
                </Typography>
              )}
            </Stack>
          </ListSubheader>
        )
      }
    >
      {items.map((item) => (
        <LinkListItem
          key={item.identifier}
          loading={loading}
          level={level}
          currentLeftGutterWidth={currentLeftGutterWidth}
          item={item}
          expandedItems={expandedItems}
          handleSubNavClick={handleSubNavClick}
          currentRightGutterWidth={currentRightGutterWidth}
        />
      ))}
    </List>
  );
}
